import { useCriarMovimento } from 'hooks/useSngpc';
import { CriarMovimentoDto } from 'data/sngpc';
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSearch } from '@fortawesome/free-solid-svg-icons';
import ModalPesquisarProdutosSngpc from '../../components/ModalPesquisarProdutosSngpc';
import { ProdutoParaInventarioType } from 'data/sngpc';

interface ModalCriarMovimentoProps {
    show: boolean;
    onHide: () => void;
    tipo: CriarMovimentoDto['tipo'] | null;
    inventarioId?: number;
}

const ModalCriarMovimento: React.FC<ModalCriarMovimentoProps> = ({ show, onHide, tipo, inventarioId }) => {
    const criarMovimentoMutation = useCriarMovimento();
    const [showPesquisaProduto, setShowPesquisaProduto] = useState(false);
    const [produtoSelecionado, setProdutoSelecionado] = useState<ProdutoParaInventarioType | null>(null);

    const {
        handleSubmit,
        register,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm<Partial<CriarMovimentoDto>>();

    const produtoId = watch('produtoId');

    useEffect(() => {
        if (show) {
            reset({
                inventarioId: inventarioId,
                tipo: tipo || undefined,
                produtoId: undefined,
                numero: '', // Lote
                quantidade: 0,
                data: new Date().toISOString().split('T')[0],
            });
            setProdutoSelecionado(null);
        }
    }, [show, tipo, inventarioId, reset]);

    useEffect(() => {
        if (!produtoId) {
            setProdutoSelecionado(null);
        }
    }, [produtoId]);

    const handleSelecionarProduto = (produto: ProdutoParaInventarioType) => {
        setProdutoSelecionado(produto);
        setValue('produtoId', produto.id, { shouldValidate: true });
        setValue('rms', produto.rms || '');
        setShowPesquisaProduto(false);
    };

    const onSubmit = (values: Partial<CriarMovimentoDto>) => {
        if (!inventarioId || !tipo || !produtoSelecionado) return;

        const dadosCompletos: CriarMovimentoDto = {
            ...values,
            inventarioId: inventarioId,
            tipo: tipo,
            produtoId: produtoSelecionado.id,
            numero: values.numero!, // Lote
            rms: values.rms!,
            quantidade: values.quantidade!,
            data: values.data!,
        };

        criarMovimentoMutation.mutate(dadosCompletos, {
            onSuccess: () => {
                onHide();
            }
        });
    };

    const getModalTitle = () => {
        switch (tipo) {
            case 'ENTRADA':
                return 'Nova Entrada de Produto Controlado';
            case 'VENDA_CONSUMIDOR':
                return 'Nova Venda de Produto Controlado';
            case 'PERDA':
                return 'Registrar Perda de Produto Controlado';
            default:
                return 'Criar Movimento';
        }
    }

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{getModalTitle()}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Produto</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            readOnly
                                            value={produtoSelecionado ? `${produtoSelecionado.codigo} - ${produtoSelecionado.nome}` : 'Clique para pesquisar um produto...'}
                                            onClick={() => setShowPesquisaProduto(true)}
                                            isInvalid={!!errors.produtoId}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Button variant="outline-secondary" onClick={() => setShowPesquisaProduto(true)}>
                                            <FontAwesomeIcon icon={faSearch} />
                                        </Button>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.produtoId?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                    <input
                                        type="hidden"
                                        {...register('produtoId', { required: 'Produto é obrigatório.' })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Lote</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('numero', { required: 'Lote é obrigatório.' })}
                                        isInvalid={!!errors.numero}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.numero?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>RMS</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('rms', { required: 'RMS é obrigatório.' })}
                                        isInvalid={!!errors.rms}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.rms?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Quantidade</Form.Label>
                                    <Form.Control
                                        type="number"
                                        {...register('quantidade', {
                                            required: 'Quantidade é obrigatória.',
                                            valueAsNumber: true,
                                            validate: value => (value && value > 0) || 'A quantidade deve ser maior que zero.'
                                        })}
                                        isInvalid={!!errors.quantidade}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.quantidade?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data do Movimento</Form.Label>
                                    <Form.Control
                                        type="date"
                                        {...register('data', { required: 'Data do movimento é obrigatória.' })}
                                        isInvalid={!!errors.data}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.data?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        {tipo === 'ENTRADA' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fornecedor</Form.Label>
                                        <Form.Control
                                            type="number"
                                            {...register('fornecedorId', { required: tipo === 'ENTRADA' ? 'Fornecedor é obrigatório.' : false })}
                                            isInvalid={!!errors.fornecedorId}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.fornecedorId?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Número da Nota Fiscal</Form.Label>
                                        <Form.Control
                                            type="text"
                                            {...register('numeroNota', { required: tipo === 'ENTRADA' ? 'Número da nota é obrigatório.' : false })}
                                            isInvalid={!!errors.numeroNota}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.numeroNota?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {tipo === 'VENDA_CONSUMIDOR' && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Médico</Form.Label>
                                        <Form.Control
                                            type="number"
                                            {...register('medicoId', { required: tipo === 'VENDA_CONSUMIDOR' ? 'Médico é obrigatório.' : false })}
                                            isInvalid={!!errors.medicoId}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.medicoId?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Número da Receita</Form.Label>
                                        <Form.Control
                                            type="text"
                                            {...register('numeroReceita', { required: tipo === 'VENDA_CONSUMIDOR' ? 'Número da receita é obrigatório.' : false })}
                                            isInvalid={!!errors.numeroReceita}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.numeroReceita?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {tipo === 'PERDA' && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Motivo da Perda</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            {...register('motivo', { required: tipo === 'PERDA' ? 'O motivo da perda é obrigatório.' : false })}
                                            isInvalid={!!errors.motivo}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.motivo?.message}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={criarMovimentoMutation.isPending}>
                            {criarMovimentoMutation.isPending ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faSave} />}
                            {' Salvar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ModalPesquisarProdutosSngpc
                show={showPesquisaProduto}
                onHide={() => setShowPesquisaProduto(false)}
                onSelecionarProduto={handleSelecionarProduto}
            />
        </>
    );
};

export default ModalCriarMovimento; 